<template>
  <article class="gs-asset-card">
    <header v-if="title || subtitle || badgeList.length" class="gs-asset-card__head">
      <div class="gs-asset-card__heading">
        <h3 v-if="title" class="gs-asset-card__title">{{ title }}</h3>
        <p v-if="subtitle" class="gs-asset-card__subtitle">{{ subtitle }}</p>
      </div>

      <ul v-if="badgeList.length" class="gs-asset-card__badges">
        <li v-for="(badge, index) in badgeList" :key="index" class="gs-asset-card__badge">
          <span class="gs-asset-card__badge-label">{{ badge.label }}</span>
          <button
            v-if="badge.copyText"
            type="button"
            class="gs-asset-card__badge-copy"
            :title="badge.copyTitle"
            :aria-label="badge.copyTitle"
            @click="copyBadge(badge)"
          >
            <gs-icon name="copy" size="12" />
          </button>
        </li>
      </ul>
    </header>

    <div v-if="$slots.toolbar" class="gs-asset-card__toolbar">
      <slot name="toolbar" />
    </div>

    <div
      v-if="$slots.preview"
      ref="preview"
      role="region"
      :aria-label="title ? title + ' preview' : 'Asset preview'"
      :tabindex="collapsible ? 0 : null"
      class="gs-asset-card__preview"
      :class="{ 'is-clamped': collapsible && !expanded }"
      :style="collapsible && !expanded ? { maxHeight: 'min(' + maxHeight + 'px, 65vh)' } : null"
    >
      <slot name="preview" />
    </div>

    <button
      v-if="collapsible && (overflowing || expanded)"
      type="button"
      class="gs-asset-card__expand"
      @click="toggle()"
    >
      <gs-icon :name="expanded ? 'chevron-up' : 'chevron-down'" size="14" />
      {{ expanded ? 'Show less' : 'Show more' }}
    </button>

    <div v-if="hasBody" class="gs-asset-card__body">
      <slot />
    </div>

    <footer v-if="$slots.actions" class="gs-asset-card__actions">
      <slot name="actions" />
    </footer>
  </article>
</template>

<script>
import { copyAsset } from './copyAsset';

export default {
  name: 'AssetCard',
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    // Either plain strings or `{ label, copyText, copyTitle }`. A badge that
    // carries `copyText` copies itself — that is how an email subject line is
    // grabbed without leaving the card.
    badges: {
      type: Array,
      default: () => [],
    },
    // Long previews (email bodies, blog posts) are clamped with a toggle so a
    // section of twenty assets stays scannable.
    collapsible: Boolean,
    maxHeight: {
      type: Number,
      default: 560,
    },
  },
  data() {
    return {
      expanded: false,
      overflowing: false,
    };
  },
  computed: {
    // A default slot that only holds whitespace still yields a text vnode, so
    // an empty body region would otherwise render its own gap.
    hasBody() {
      const nodes = this.$slots.default;

      if (!nodes || !nodes.length) {
        return false;
      }

      return nodes.some(node => node.tag || (node.text && node.text.trim()));
    },
    badgeList() {
      return (this.badges || [])
        .map(badge => (typeof badge === 'string' ? { label: badge } : badge))
        .filter(badge => badge && badge.label)
        .map(badge => ({
          label: badge.label,
          copyText: badge.copyText || '',
          copyTitle: badge.copyTitle || `Copy "${badge.label}"`,
        }));
    },
  },
  created() {
    // Non-reactive bookkeeping for the size watchers set up in `syncWatchers`.
    this.resizeObserver = null;
    this.watchedNodes = [];
    this.loadTarget = null;
    this.loadHandler = null;
  },
  mounted() {
    this.measure();
    this.syncWatchers();
  },
  updated() {
    this.measure();
    this.syncWatchers();
  },
  beforeDestroy() {
    this.stopWatching();
  },
  methods: {
    // An image inside the preview usually arrives after the first paint, and
    // the clamped well cannot grow with it, so nothing would tell the card it
    // had become taller than the clamp. Watch the preview and its children for
    // size changes instead of measuring once and trusting it forever.
    syncWatchers() {
      const preview = this.$refs.preview;

      if (!this.collapsible || !preview) {
        this.stopWatching();
        return;
      }

      const nodes = [preview].concat(Array.prototype.slice.call(preview.children));
      const unchanged = nodes.length === this.watchedNodes.length
        && nodes.every((node, index) => node === this.watchedNodes[index]);

      if (unchanged && (this.resizeObserver || this.loadHandler)) {
        return;
      }

      this.stopWatching();
      this.watchedNodes = nodes;

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        nodes.forEach(node => this.resizeObserver.observe(node));
        return;
      }

      // Older browsers: `load` does not bubble, so listen in the capture phase
      // on the well itself and re-measure when its images arrive.
      this.loadHandler = () => this.measure();
      this.loadTarget = preview;
      preview.addEventListener('load', this.loadHandler, true);
    },
    stopWatching() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.loadTarget && this.loadHandler) {
        this.loadTarget.removeEventListener('load', this.loadHandler, true);
      }

      this.loadTarget = null;
      this.loadHandler = null;
      this.watchedNodes = [];
    },
    measure() {
      if (!this.collapsible || this.expanded) {
        return;
      }

      const preview = this.$refs.preview;

      if (!preview) {
        return;
      }

      // 4px of slack keeps sub-pixel line heights from showing a toggle that
      // reveals nothing.
      this.overflowing = preview.scrollHeight > preview.clientHeight + 4;
    },
    toggle() {
      this.expanded = !this.expanded;
    },
    async copyBadge(badge) {
      try {
        await copyAsset({ text: badge.copyText }, value => this.$copyText(value));
        this.$emit('copy-badge', badge.copyText);
        this.$gsToast.success('Copied');
      } catch (error) {
        this.$gsToast.error('Could not copy');
      }
    },
  },
};
</script>

<style scoped>
.gs-asset-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 16px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-asset-card__head {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: flex-start;
  justify-content: space-between;
  min-width: 0;
}

.gs-asset-card__heading {
  flex: 1 1 180px;
  min-width: 0;
}

.gs-asset-card__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.gs-asset-card__subtitle {
  margin: 3px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.gs-asset-card__badges {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.gs-asset-card__badge {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  max-width: 100%;
  min-height: 24px;
  padding: 3px 4px 3px 9px;
  color: var(--gs-muted-foreground);
  background: var(--gs-muted);
  border: 1px solid var(--gs-border);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.gs-asset-card__badge-label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.gs-asset-card__badge-copy {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.gs-asset-card__badge-copy:hover {
  color: var(--gs-primary);
  background: var(--gs-surface);
}

.gs-asset-card__badge-copy:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-asset-card__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

/* The three lines carrying `!important` are a security guard, not a
   typographic preference: the preview holds a vendor's own stylesheet, which
   the sanitiser has already confined to selectors under this well but which
   can still style the well's own contents. `position: relative` plus
   `contain: layout paint` makes this box the containing block for every
   absolutely *and* fixed positioned descendant and clips their paint to it, so
   `body { position: fixed; inset: 0 }` in a swipe becomes a full-bleed banner
   inside its own card rather than an overlay across the affiliate portal. */
.gs-asset-card__preview {
  position: relative !important;
  min-width: 0;
  overflow: hidden !important;
  contain: layout paint;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-asset-card__preview.is-clamped {
  overflow: auto !important;
  max-height: min(560px, 65vh);
  scrollbar-gutter: stable;
  -webkit-overflow-scrolling: touch;
}

.gs-asset-card__preview.is-clamped::after {
  content: none;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 48px;
  background: linear-gradient(to bottom, transparent, var(--gs-surface-subtle));
  pointer-events: none;
}

.gs-asset-card__expand {
  display: inline-flex;
  align-self: flex-start;
  gap: 5px;
  align-items: center;
  padding: 4px 8px;
  margin: -4px 0 0 -8px;
  color: var(--gs-muted-foreground);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
}

.gs-asset-card__expand:hover {
  color: var(--gs-foreground);
  background: var(--gs-muted);
}

.gs-asset-card__expand:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-asset-card__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.gs-asset-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

@media (max-width: 480px) {
  .gs-asset-card {
    padding: 14px;
  }

  .gs-asset-card__actions ::v-deep .gs-button {
    flex: 1 1 140px;
  }
}
</style>
